import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ConfirmDialogComponent,
  ConfirmVariant,
} from '../components/confirm-dialog/confirm-dialog.component';

/** Opciones del dialogo de confirmacion. Todo el texto va en espanol. */
export interface ConfirmOptions {
  title?: string;
  message?: string;
  /** Detalle opcional: el nombre del registro afectado, por ejemplo. */
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` (rojo) para borrados fisicos; `warning` (ambar) para desactivar; `primary` para el resto. */
  variant?: ConfirmVariant;
}

/**
 * Abre el dialogo de confirmacion y resuelve a `true` solo si el usuario
 * confirma. Cancelar, pulsar Escape o hacer clic fuera resuelven `false`.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly modal = inject(NgbModal);

  async ask(options: ConfirmOptions = {}): Promise<boolean> {
    const reference = this.modal.open(ConfirmDialogComponent, {
      centered: true,
      size: 'sm',
      backdrop: true,
      ariaLabelledBy: 'ks-confirm-title',
    });

    const dialog = reference.componentInstance as ConfirmDialogComponent;

    if (options.title) dialog.title.set(options.title);
    if (options.message) dialog.message.set(options.message);
    if (options.detail) dialog.detail.set(options.detail);
    if (options.confirmLabel) dialog.confirmLabel.set(options.confirmLabel);
    if (options.cancelLabel) dialog.cancelLabel.set(options.cancelLabel);
    if (options.variant) dialog.variant.set(options.variant);

    try {
      return (await reference.result) === true;
    } catch {
      // Descartado (Escape, clic fuera o boton Cancelar).
      return false;
    }
  }

  /**
   * Atajo para BORRADO FISICO: variante roja y textos ya redactados.
   *
   * Uso exclusivo de los endpoints que borran de verdad, hoy solo
   * `DELETE /prefills/{id}/values/{id}`. Para los soft-deletes del API
   * (`status = false`) usa `askDeactivate`: decir "permanente" ahi es mentira.
   */
  askDelete(entity: string, detail = ''): Promise<boolean> {
    return this.ask({
      title: `Eliminar ${entity}`,
      message: `Se eliminará ${entity} de forma permanente. Esta acción no se puede deshacer.`,
      detail,
      confirmLabel: 'Eliminar',
      variant: 'danger',
    });
  }

  /**
   * Atajo para los soft-deletes del API: el registro solo cambia a
   * `status = false`, no se borra, asi que el tono es de advertencia (ambar)
   * y no de borrado (rojo).
   *
   * `entity` se escribe CON articulo y en minusculas ('la plantilla',
   * 'el tipo de campo'); el titulo lo quita solo.
   */
  askDeactivate(entity: string, detail = ''): Promise<boolean> {
    return this.ask({
      title: `Desactivar ${stripArticle(entity)}`,
      message:
        `Se desactivará ${entity} y dejará de ofrecerse en la app. ` +
        `No se borra nada: puedes volver a activar ${entity} cuando quieras.`,
      detail,
      confirmLabel: 'Desactivar',
      variant: 'warning',
    });
  }
}

/** 'la plantilla' -> 'plantilla'. El titulo se lee mejor sin articulo. */
const stripArticle = (entity: string): string =>
  entity.replace(/^(el|la|los|las|un|una|unos|unas)\s+/i, '');
